import { WorkerRequirementDomain } from "@clipboard-health/contract-worker-app-bff";
import { CardWithIntent, Illustration, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack, useMediaQuery } from "@mui/material";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import pluralize from "pluralize";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { DOCUMENT_DETAILS_FULL_PATH, WORKPLACE_DOCUMENTS_FULL_PATH } from "../../Documents/paths";
import { type PlacementDetail } from "../api/useGetPlacement";
import { useHiringQualificationRanking } from "../api/useHiringQualificationRanking";
import { usePlacementRecruiterScreeningConfig } from "../hooks/usePlacementRecruiterScreeningConfig";
import { shouldShowRequirement } from "../utils";

export function PlacementDocumentRequirementsCard(props: { placementDetails: PlacementDetail }) {
  const { placementDetails } = props;

  const worker = useDefinedWorker();
  const history = useHistory();
  const { getHighestRankingQualification } = useHiringQualificationRanking();
  const { legacyDocumentRequirementId: legacyScreeningDocumentRequirementId } =
    usePlacementRecruiterScreeningConfig();

  const requiredDocuments = placementDetails.workerRequirements?.filter((requirement) =>
    shouldShowRequirement(requirement, legacyScreeningDocumentRequirementId)
  );

  const highestRankingSharedQualification = getHighestRankingQualification(
    (placementDetails.workerTypes ?? []).filter((qualification) =>
      worker.licensesData.map((licenseData) => licenseData.qualification).includes(qualification)
    )
  );
  const isSmallVerticalSpacing = useMediaQuery("(max-height: 700px)");
  const isSmallHorizontalSpacing = useMediaQuery("(max-width: 350px)");

  const workplaceDocumentsPath = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
    workplaceId: placementDetails.workplace.id,
  });

  const requiredDocumentsCount = requiredDocuments?.length ?? 0;

  if (requiredDocumentsCount === 0) {
    return null;
  }

  const redirectToRequirements = () => {
    if (isDefined(requiredDocuments) && requiredDocumentsCount === 1) {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: requiredDocuments[0].relationships.requirement.data.id,
        })
      );
      return;
    }

    if (isDefined(highestRankingSharedQualification)) {
      history.push(
        `${workplaceDocumentsPath}?domain=${WorkerRequirementDomain.HIRING}&qualification=${highestRankingSharedQualification}`
      );
    } else {
      history.push(workplaceDocumentsPath);
    }
  };

  return (
    <CardWithIntent intent="neutral">
      <CardContent
        sx={{
          py: isSmallVerticalSpacing ? 1 : undefined,
          px: isSmallHorizontalSpacing ? 2 : 6,
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={isSmallVerticalSpacing ? 2 : 5}
          sx={{
            textAlign: "center",
            textWrap: "balance",
            width: "100%",
            flexGrow: 1,
            mb: "auto",
          }}
        >
          <Illustration type="document-upload" />

          <Text semibold variant="h5">
            This workplace requires {requiredDocumentsCount}{" "}
            {pluralize("document", requiredDocumentsCount)}
          </Text>

          <Text
            color={(theme) => theme.palette.text.secondary}
            variant="body2"
            data-testid="bookability-sheet-description"
          >
            Please complete {requiredDocumentsCount} {pluralize("document", requiredDocumentsCount)}{" "}
            to apply to this job
          </Text>
        </Stack>

        <Box sx={{ width: "100%", mt: isSmallVerticalSpacing ? 2 : 5 }}>
          <Button fullWidth variant="contained" size="medium" onClick={redirectToRequirements}>
            View details
          </Button>
        </Box>
      </CardContent>
    </CardWithIntent>
  );
}
